import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import {
  JsonKeyValueTable,
  rendersAsTable,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@resource-kit/ui/react';
import {
  CopyJsonButton,
  ExpandJsonButton,
  JsonTableToolbar,
} from '@components/ui/json-panel-chrome';

export interface JsonPanelProps {
  value: unknown;
  /** Accessible name for the underlying table (and dialog title fallback). */
  ariaLabel?: string;
  /** Optional label shown left of the affordance buttons above the table. */
  label?: ReactNode;
  /** Dialog header title; defaults to `label` then `ariaLabel`. */
  title?: ReactNode;
  className?: string;
}

/**
 * Wraps `JsonKeyValueTable` with two operator affordances for long raw
 * payloads: copy-the-raw-JSON and expand-into-a-near-fullscreen dialog.
 * Kept as a thin wrapper so any call site rendering multi-KB JSON (order
 * trace webhook bodies, #1203) gets both for free.
 *
 * The buttons now sit inside the table's own border rather than floating above
 * it, so a JSON panel reads as one object instead of a table with a detached
 * button row. They are still rendered HERE and handed down as a slot: the table
 * is rendered twice — inline and inside the dialog — and only the caller can
 * know that the second one must not offer to open a dialog it is already in.
 */
export function JsonPanel({ value, ariaLabel, label, title, className }: JsonPanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dialogTitle = title ?? label ?? ariaLabel ?? t('json.tableAria');

  const toolbar = (
    <JsonTableToolbar label={label}>
      <CopyJsonButton value={value} />
      <ExpandJsonButton onClick={() => setOpen(true)} />
    </JsonTableToolbar>
  );

  // An empty or scalar value renders as a bare span with no border to sit in,
  // so the toolbar goes above it instead of into a slot that will not exist.
  const hasTable = rendersAsTable(value);

  return (
    <div className={cn('space-y-1', className)}>
      {hasTable ? (
        <JsonKeyValueTable value={value} ariaLabel={ariaLabel} toolbar={toolbar} />
      ) : (
        <>
          {toolbar}
          <JsonKeyValueTable value={value} ariaLabel={ariaLabel} />
        </>
      )}

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        size="full"
        className="flex h-[85vh] flex-col"
      >
        <ModalHeader onClose={() => setOpen(false)}>
          <div className="flex items-center gap-2">
            <ModalTitle>{dialogTitle}</ModalTitle>
            <CopyJsonButton value={value} />
          </div>
        </ModalHeader>
        <ModalBody className="min-h-0 flex-1 overflow-auto">
          {/* No toolbar: copy lives in the dialog header, and offering
              "expand" inside the expanded view is the nested-fullscreen bug. */}
          <JsonKeyValueTable value={value} ariaLabel={ariaLabel} />
        </ModalBody>
      </Modal>
    </div>
  );
}

export default JsonPanel;
